import type { LocationPoint, StatusResponse } from "./types";
import { formatDdm, formatUtm } from "./coordinates";
import { ageLabel } from "./track";

export function buildEmergencySummary(
  status: StatusResponse,
  now = new Date()
): string {
  const point = status.latestLocation;
  if (!point) {
    return "Emergency location information for Neil.\n\nNo reported tracking location is currently available.";
  }

  const lines = [
    "Emergency location information for Neil.",
    "",
    `His last reported location was recorded at ${formatTimestamp(point.recordedAt)}, approximately ${ageLabel(point.recordedAt, now)}.`,
    "",
    `Latitude: ${point.latitude}.`,
    `Longitude: ${point.longitude}.`,
    `Degrees decimal minutes: ${formatDdm(point.latitude, point.longitude)}.`,
    `UTM: ${formatUtm(point.latitude, point.longitude)}.`,
    "",
    `The reported GPS accuracy was approximately plus or minus ${Math.round(point.accuracyMeters)} metres.`
  ];

  if (isStale(point, now)) {
    lines.push("This location is delayed or stale. Do not describe it as live.");
  }

  lines.push(
    `Tracking is currently ${
      status.trackingActive == null ? "unknown" : status.trackingActive ? "active" : "stopped"
    }.`
  );

  if (status.latestSessionStartedAt) {
    lines.push(
      `The current tracking session began at ${formatTimestamp(status.latestSessionStartedAt)}.`
    );
  }

  if (status.quickMessage && status.quickMessageRecordedAt) {
    lines.push(
      `His latest status message was "${status.quickMessage}", sent at ${formatTimestamp(status.quickMessageRecordedAt)}.`
    );
  }

  if (point.altitudeMeters !== null) {
    lines.push(`Altitude: approximately ${Math.round(point.altitudeMeters)} metres.`);
  }
  if (point.speedMps !== null) {
    lines.push(`Speed: approximately ${point.speedMps.toFixed(1)} metres per second.`);
  }
  if (point.bearingDegrees !== null) {
    lines.push(`Bearing: approximately ${Math.round(point.bearingDegrees)} degrees.`);
  }
  if (point.batteryPercentage !== null) {
    const charging = point.isCharging === null
      ? ""
      : point.isCharging
        ? ", charging"
        : ", not charging";
    lines.push(`Device battery: ${point.batteryPercentage}%${charging}.`);
  }

  lines.push(`This point was uploaded to the server at ${formatTimestamp(point.serverReceivedAt)}.`);

  return lines.join("\n");
}

export function buildShareText(
  summary: string,
  point: LocationPoint | null,
  emergencyLink: string | null
): string {
  const parts = [summary];
  if (point) {
    parts.push(
      `Open in Maps: ${mapsUrl(point.latitude, point.longitude)}`
    );
  }
  if (emergencyLink) parts.push(`Temporary emergency link: ${emergencyLink}`);
  return parts.join("\n\n");
}

export function mapsUrl(latitude: number, longitude: number): string {
  return `https://www.google.com/maps/search/?api=1&query=${latitude},${longitude}`;
}

export function isStale(point: LocationPoint, now = new Date()): boolean {
  return now.getTime() - Date.parse(point.recordedAt) > 30 * 60 * 1000;
}

function formatTimestamp(value: string): string {
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "medium"
  }).format(new Date(value));
}
